import {
  clearAnalyticsOptOut,
  identifyOrganization,
  identifyUser,
  isExcludedEmail,
  optOutAnalytics,
  readAnalyticsOptOut,
  resetAnalytics,
  resolveCookieDomain,
} from "@mountayaapp/analytics";
import type { UserOrganization } from "@mountayaapp/api";
import type { IdentitySession, IdentityStoreApi, Traits } from "@mountayaapp/identity";

import { env } from "../../env";

/**
 * identified caches the account id already sent to PostHog. The _app guard that
 * calls identifyOnce re-runs on every client navigation and on every hover
 * preload, so without this the same person would be re-identified dozens of times
 * a session. It is module-scoped for the same reason the session bootstrap cache
 * is: the auth flows navigate with a full page load, which reboots the module.
 */
let identified: string | null = null;

/**
 * ANALYTICS_COOKIE_DOMAIN scopes the opt-out to every Mountaya property rather
 * than to this origin alone. The application is the only surface that can classify
 * a person — it is the only one that knows who is signed in — so it is also the
 * only one that writes this, and the documentation site reads it back on the
 * shared parent domain.
 *
 * It derives from the application's own origin, which is required configuration
 * this app cannot boot without, so the two can never be configured apart.
 */
const ANALYTICS_COOKIE_DOMAIN = resolveCookieDomain(env.rootDomain);

/**
 * applyAnalyticsPolicy decides whether the person who just signed in reports to
 * PostHog at all, and is the only place that decision is made. It runs the moment
 * the Ory session resolves, which is the earliest the email exists: the SDK is
 * already initialized by then, because replay records only from the moment the
 * recorder starts and deferring the start would cost every real visitor the
 * opening of their session.
 *
 * The readmission arm is gated on the opt-out actually being set. It is what
 * handles an address changed through the Ory settings flow and a different person
 * signing in on the same browser, but an ordinary visitor must not have PostHog's
 * consent state rewritten on every page load.
 */
export const applyAnalyticsPolicy = (session: IdentitySession): void => {
  if (isExcludedEmail(session.traits.email)) {
    optOutAnalytics(ANALYTICS_COOKIE_DOMAIN);

    return;
  }

  if (readAnalyticsOptOut()) {
    clearAnalyticsOptOut(ANALYTICS_COOKIE_DOMAIN);
  }
};

/**
 * identifySession links the current person to PostHog and attaches the profile
 * properties dashboards segment on. It always sends, so a settings save that
 * changed a trait pushes the new value; identifyOnce is the deduplicated variant
 * for the guard that re-runs constantly.
 *
 * Membership is the commercial fact about a person (a person buys nothing, so belonging to
 * an organization is what segments them): `organization_member` for belonging to any, and
 * `studio_member` for belonging to one whose agreement includes Map Studio. Both are read
 * from the store rather than from the Ory metadata, which carries neither.
 */
export const identifySession = (store: IdentityStoreApi): void => {
  const state = store.getState();
  if (!state.userId) {
    return;
  }

  identified = state.userId;

  identifyUser(state.userId, {
    email: state.traits?.email,
    email_verified: state.isEmailVerified,
    language: state.preferences?.language,
    name: fullName(state.traits),
    newsletter: state.traits?.newsletter,
    organization_member: state.organizationIds.length > 0,
    studio_member: state.studioOrganizationIds.length > 0,
  });
};

/**
 * identifyOnce identifies the person unless that same account id has already been
 * sent this page load.
 */
export const identifyOnce = (store: IdentityStoreApi): void => {
  if (identified !== null && identified === store.getState().userId) {
    return;
  }

  identifySession(store);
};

/**
 * identifyActiveOrganization scopes subsequent events to an organization, which
 * is what makes usage answerable per customer rather than only per person. The
 * display name comes from the membership list the guards have already resolved,
 * so this costs no extra request.
 *
 * Only organization-level facts are sent as group properties. The viewer's own
 * role is deliberately not among them: a group property is shared, so writing it
 * here would have each visitor overwrite the organization's role with their own.
 * The organization's lifecycle status is not among them either: a membership no
 * longer carries it, and every organization listed is one the viewer works in.
 */
export const identifyActiveOrganization = (
  organizationId: string,
  organizations: readonly UserOrganization[],
): void => {
  const membership = organizations.find(
    (candidate) => candidate.organization_id === organizationId,
  );

  identifyOrganization(organizationId, { name: membership?.organization_name });
};

/**
 * resetIdentification unlinks the device on logout, so the next person to sign in
 * on a shared browser does not inherit the previous one's identity. It clears the
 * local dedupe cache too, so a re-login on the same page load identifies again.
 *
 * The opt-out is cleared unconditionally rather than only for an excluded person:
 * signing out is exactly the moment the device stops belonging to whoever set it,
 * and leaving it in place would silence the next person to sign in here — and,
 * because the decision reaches every property, on the documentation site too.
 */
export const resetIdentification = (): void => {
  identified = null;
  clearAnalyticsOptOut(ANALYTICS_COOKIE_DOMAIN);
  resetAnalytics();
};

/**
 * fullName joins the identity traits into the display name PostHog shows on a
 * person. Either half may be empty, so the join is trimmed and an entirely empty
 * name is sent as undefined rather than as a blank string.
 */
const fullName = (traits: Traits | null): string | undefined => {
  if (!traits) {
    return undefined;
  }

  return `${traits.first_name} ${traits.last_name}`.trim() || undefined;
};
